import CompanyLayout from '@/components/feature/CompanyLayout';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

const developerNavItems = [
  { path: '/api-reference', label: 'API Reference' },
  { path: '/sdks', label: 'SDKs' },
];

const sdks = [
  {
    name: 'Python SDK',
    version: 'v2.1.0',
    descKey: 'sdks.pythonDesc',
    descDefault: 'Full-featured Python client for REST API and WebSocket streaming. Supports async/await, type hints, and automatic reconnection.',
    install: 'pip install zhisu-protocol',
    docs: 'https://docs.zhisu.trade/python',
    github: 'https://github.com/zhisu-protocol/python-sdk',
  },
  {
    name: 'TypeScript SDK',
    version: 'v1.8.3',
    descKey: 'sdks.tsDesc',
    descDefault: 'Modern TypeScript/JavaScript client with full type safety, built-in retry logic, and browser/Node.js compatibility.',
    install: 'npm install @zhisu-protocol/sdk',
    docs: 'https://docs.zhisu.trade/typescript',
    github: 'https://github.com/zhisu-protocol/ts-sdk',
  },
  {
    name: 'Rust SDK',
    version: 'v0.9.2',
    descKey: 'sdks.rustDesc',
    descDefault: 'High-performance Rust client optimized for low-latency trading bots and quantitative strategies.',
    install: 'cargo add zhisu-protocol',
    docs: 'https://docs.zhisu.trade/rust',
    github: 'https://github.com/zhisu-protocol/rust-sdk',
  },
  {
    name: 'Go SDK',
    version: 'v1.2.0',
    descKey: 'sdks.goDesc',
    descDefault: 'Lightweight Go client with concurrent WebSocket support and efficient memory management for high-frequency applications.',
    install: 'go get github.com/zhisu-protocol/go-sdk',
    docs: 'https://docs.zhisu.trade/go',
    github: 'https://github.com/zhisu-protocol/go-sdk',
  },
];

export default function SDKsPage() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const bgCard = isDark ? 'bg-[#0a0a0a]' : 'bg-white';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#888]' : 'text-[#666]';
  const textMuted = isDark ? 'text-[#555]' : 'text-[#999]';

  return (
    <CompanyLayout
      title={t('sdks.title', 'SDKs')}
      subtitle={t('sdks.subtitle', 'Official SDKs for Python, TypeScript, Rust, and Go')}
      category="Developers"
      navItems={developerNavItems}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sdks.map((sdk) => (
          <div key={sdk.name} className={`p-5 rounded-xl border ${borderLight} ${bgCard}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`font-semibold ${textPrimary}`}>{sdk.name}</h3>
              <span className={`text-xs px-2 py-1 rounded-full border ${borderLight} ${textMuted}`}>{sdk.version}</span>
            </div>
            <p className={`text-sm mb-4 leading-relaxed ${textSecondary}`}>{t(sdk.descKey, sdk.descDefault)}</p>
            <div className={`p-3 rounded-lg font-mono text-sm mb-4 ${isDark ? 'bg-black/40' : 'bg-[#f0f0f0]'} ${textPrimary}`}>
              {sdk.install}
            </div>
            <div className="flex items-center gap-3">
              <a href={sdk.docs} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-2 rounded-lg bg-[#00ff88]/10 border border-[#00ff88]/30 text-[#00ff88] hover:bg-[#00ff88]/20 transition-colors whitespace-nowrap">
                {t('sdks.documentation', 'Documentation')}
              </a>
              <a href={sdk.github} target="_blank" rel="noopener noreferrer" className={`text-xs px-3 py-2 rounded-lg border ${borderLight} ${textSecondary} hover:text-[#00ff88] transition-colors whitespace-nowrap`}>
                GitHub
              </a>
            </div>
          </div>
        ))}
      </div>
    </CompanyLayout>
  );
}